import { useState } from 'react';
import { TagChip } from '@/components/leads/TagChips';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast-context';
import { useBulkTag, useCatalogs } from '@/lib/queries';

/** Agregar o quitar etiquetas a los leads seleccionados en la tabla (una sola empresa). */
export function BulkTagDialog({
  companyId,
  leadIds,
  onClose,
  onDone,
}: {
  companyId: string;
  leadIds: string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { data: catalogs } = useCatalogs(companyId);
  const bulk = useBulkTag();
  const toast = useToast();
  const [mode, setMode] = useState<'add' | 'remove'>('add');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const tags = catalogs?.tags ?? [];
  const toggle = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const apply = async () => {
    try {
      const r = await bulk.mutateAsync({
        leadIds,
        add: mode === 'add' ? [...selected] : [],
        remove: mode === 'remove' ? [...selected] : [],
      });
      toast.success(
        `${r.updated} lead${r.updated === 1 ? '' : 's'} ${mode === 'add' ? 'etiquetado' : 'actualizado'}${r.updated === 1 ? '' : 's'}`,
      );
      onDone();
    } catch (err) {
      toast.error('No se pudo etiquetar', (err as Error).message);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-testid="bulk-tag-dialog">
        <DialogHeader>
          <DialogTitle>
            Etiquetar {leadIds.length} lead{leadIds.length === 1 ? '' : 's'}
          </DialogTitle>
          <DialogDescription>
            Las etiquetas se agregan a las que ya tiene cada lead (o se quitan, si eliges quitar).
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-4 text-sm">
          <label className="flex items-center gap-1.5">
            <input
              type="radio"
              name="bulk-tag-mode"
              checked={mode === 'add'}
              onChange={() => setMode('add')}
            />
            Agregar
          </label>
          <label className="flex items-center gap-1.5">
            <input
              type="radio"
              name="bulk-tag-mode"
              checked={mode === 'remove'}
              onChange={() => setMode('remove')}
            />
            Quitar
          </label>
        </div>
        {tags.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            La empresa aún no tiene etiquetas: créalas en Administración → Etiquetas y campos.
          </p>
        ) : (
          <ul className="grid gap-1 sm:grid-cols-2">
            {tags.map((t) => (
              <li key={t.id}>
                <label className="hover:bg-accent flex cursor-pointer items-center gap-2 rounded-md border px-2 py-1.5 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={selected.has(t.id)}
                    onChange={() => toggle(t.id)}
                  />
                  <TagChip tag={t} />
                </label>
              </li>
            ))}
          </ul>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={() => void apply()}
            disabled={selected.size === 0 || bulk.isPending}
            data-testid="btn-apply-bulk-tags"
          >
            {mode === 'add' ? 'Agregar etiquetas' : 'Quitar etiquetas'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
